import type { ComponentType } from 'react';
import type { Family } from './family-type';
import { CobroTabDemo } from '../paciente/CobroTab.demo';
import { CobroTabSugerencia } from '../paciente/CobroTab.sugerencia';

// =============================================================================
// Aislamiento de frontend por familia (Fase 2, ítem 8) — mismo patrón que
// packages/web/src/web/pages/admin/family-overrides.ts (CRM), SIN duplicar
// código mientras no haga falta.
//
// Hoy las vistas de hce-chart son 100% genéricas: la misma vista sirve a
// "demo", "facilitaclinic", "multiples-sedes" y "almacenes-espana". Duplicar
// páginas de una vez, sin que exista todavía ninguna diferencia real, sería
// puro riesgo de mantenimiento sin ningún beneficio hoy.
//
// En vez de eso, este registro es el ENGANCHE: cuando se pida un cambio
// futuro solo para una familia, ese cambio vive en un archivo NUEVO y
// SEPARADO que la otra familia jamás importa — la garantía de aislamiento la
// da el grafo de imports, no una condición dentro de un archivo compartido.
//
// Cómo usarlo cuando haga falta un cambio solo para una familia (ejemplo:
// tope de descuento en el cobro de facilitaclinic):
//   1. Crear `paciente/CobroTab.facilitaclinic.tsx`, un fork del componente
//      de vista actual (no del archivo entero si es evitable).
//   2. Registrar `cobro: { facilitaclinic: CobroTabFacilitaclinic }` abajo.
//   3. `CobroTab.tsx` (genérico) queda intacto — nadie en su código importa
//      el fork.
// =============================================================================

// La mayoría son rutas raíz reales de routes.tsx; "cobro" es la excepción a
// propósito — es una pestaña anidada de la ficha del paciente (:id/cobro),
// no una ruta top-level. El registro forkea VISTAS, no necesariamente rutas.
export type HceChartPageKey = 'dashboard' | 'pacientes' | 'historias' | 'agenda' | 'reportes' | 'cobro' | 'farmacia';

// "cobro": {demo: ...} — al revés del ejemplo del comentario de arriba (que
// suele forkear la familia RESTRINGIDA): acá se forkea "demo" porque es la
// familia MÁS permisiva (precio editable + descuento sin tope) — dejar el
// genérico intacto evita que facilitaclinic/multiples-sedes queden expuestas
// a esa misma libertad mientras no exista todavía su propio fork con tope.
// facilitaclinic/multiples-sedes (Fase 2, ítem 9b) comparten un solo fork
// (`CobroTabSugerencia`, texto/UI idéntico entre ambas) — precio de catálogo
// de solo lectura + sugerencia de descuento no binding. Ver CobroTab.tsx.
export const OVERRIDES: Partial<Record<HceChartPageKey, Partial<Record<Family, ComponentType<any>>>>> = {
  cobro: { demo: CobroTabDemo, facilitaclinic: CobroTabSugerencia, "multiples-sedes": CobroTabSugerencia },
};

/** `OVERRIDES[page]?.[family] ?? Default` — la única regla de resolución, para no repetirla distinto en cada vista. */
export function vistaDeFamilia<P extends object>(page: HceChartPageKey, family: Family, Default: ComponentType<P>): ComponentType<P> {
  return (OVERRIDES[page]?.[family] as ComponentType<P> | undefined) ?? Default;
}
